import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Link as RouterLink, useLoaderData, useNavigate } from 'react-router-dom';
import { useMemo } from 'react';
import { apiAdmin } from '@/api/admin';
import type { BrandDto, ProductDto } from '@/api/types';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { PageHeader } from '@/components/PageHeader';
import { useCopy } from '@/hooks/useCopy';
import { getProducts } from '@/api/lookupCache';

export interface BrandsListLoaderData {
  brands: BrandDto[];
  products: ProductDto[];
}

export async function brandsListLoader(): Promise<BrandsListLoaderData> {
  const [brands, products] = await Promise.all([
    apiAdmin.brands.list(),
    getProducts().catch(() => [] as ProductDto[]),
  ]);
  return { brands, products };
}

export function BrandsList() {
  const { brands, products } = useLoaderData() as BrandsListLoaderData;
  const { t } = useCopy();
  const navigate = useNavigate();
  const productMap = useMemo(() => {
    const m = new Map<number, ProductDto>();
    products.forEach((p) => m.set(p.id, p));
    return m;
  }, [products]);

  return (
    <Box>
      <Breadcrumbs
        items={[{ label: t('nav.home', 'Home'), to: '/' }, { label: t('nav.brands', 'Brands') }]}
      />
      <PageHeader
        title={t('brands.list.title', 'Brands')}
        subtitle={t('brands.list.subtitle', '')}
        actions={
          <Button
            component={RouterLink}
            to="/brands/new"
            variant="contained"
            startIcon={<AddIcon />}
          >
            {t('brands.list.create', 'Create brand')}
          </Button>
        }
      />
      {brands.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('brands.list.empty', 'No brands yet.')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Logo</TableCell>
                <TableCell>{t('brands.fields.displayName', 'Name')}</TableCell>
                <TableCell>{t('brands.fields.slug', 'Slug')}</TableCell>
                <TableCell>{t('brands.fields.product', 'Product')}</TableCell>
                <TableCell>Colors</TableCell>
                <TableCell>Providers</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {brands.map((b) => (
                <TableRow
                  key={b.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/brands/${b.id}`)}
                >
                  <TableCell>
                    {b.logoUrl ? (
                      <Box
                        component="img"
                        src={b.logoUrl}
                        sx={{ height: 28, maxWidth: 80, objectFit: 'contain' }}
                        alt=""
                      />
                    ) : (
                      <Box sx={{ width: 28, height: 28, bgcolor: 'grey.200', borderRadius: 1 }} />
                    )}
                  </TableCell>
                  <TableCell>{b.displayName}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {b.slug}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {productMap.get(b.productId)?.displayName ?? `#${b.productId}`}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {['primary', 'background', 'text'].map((k) => {
                        const v = (b.colors as Record<string, unknown>)[k];
                        if (typeof v !== 'string') return null;
                        return (
                          <Box
                            key={k}
                            sx={{
                              width: 14,
                              height: 14,
                              borderRadius: '50%',
                              bgcolor: v,
                              border: '1px solid rgba(0,0,0,0.12)',
                            }}
                          />
                        );
                      })}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {b.authProviders.map((p) => (
                        <Chip key={p} label={p} size="small" />
                      ))}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
